/**
 * Lazy loader for the brightmark capture library (#1998).
 *
 * The capture library (`frontend/src/lib/brightmark/`) pulls in `@medv/finder` and `@zumer/snapdom`
 * (snapdom alone is sizeable). To keep the main bundle lean it is loaded via a single dynamic
 * `import()` the first time the user enters annotate mode. The promise is memoised so a second entry
 * reuses the already-resolved module instead of re-importing.
 *
 * We import only the functions the annotate UX uses; tree-shaking drops the rest of the barrel.
 */
import type { BrightmarkAnnotation } from '@/lib/brightmark/types'

/** The subset of the brightmark public surface the annotate UX consumes. */
export interface BrightmarkLib {
  buildElementAnnotation: (el: Element, event: MouseEvent) => BrightmarkAnnotation
  buildTextAnnotation: (container: Element, range: Range) => BrightmarkAnnotation
  captureElement: (
    el: Element
  ) => Promise<{ data_url: string; format: 'png'; width: number; height: number } | null>
  dataUrlToBlob: (dataUrl: string) => Blob
  createHoverHighlighter: () => { enable: () => void; disable: () => void }
  isAgentUi: (el: Element | null) => boolean
}

let cached: Promise<BrightmarkLib> | null = null

/** Load (and memoise) the brightmark capture library on first use of annotate mode. */
export function loadBrightmark(): Promise<BrightmarkLib> {
  if (!cached) {
    cached = import('@/lib/brightmark').then((m) => ({
      buildElementAnnotation: m.buildElementAnnotation,
      buildTextAnnotation: m.buildTextAnnotation,
      captureElement: m.captureElement,
      dataUrlToBlob: m.dataUrlToBlob,
      createHoverHighlighter: m.createHoverHighlighter,
      isAgentUi: m.isAgentUi,
    }))
  }
  return cached
}
